import React, { useState, useEffect } from 'react';
import { useSlideshow } from '../contexts/SlideshowContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Slideshow.css';

function Slideshow() {
  const { getActiveSlides, loading, error } = useSlideshow();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Get only active slides
  const activeSlides = getActiveSlides();
  
  // Auto-advance the slideshow
  useEffect(() => {
    // Only auto-advance if there are multiple slides
    if (activeSlides.length <= 1) return;
    
    const interval = setInterval(() => {
      goToNextSlide();
    }, 5000); // Change slide every 5 seconds
    
    return () => clearInterval(interval);
  }, [activeSlides, currentSlide]);
  
  // Handle navigation
  const goToNextSlide = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev === activeSlides.length - 1 ? 0 : prev + 1));
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };
  
  const goToPrevSlide = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev === 0 ? activeSlides.length - 1 : prev - 1));
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };
  
  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    
    setIsTransitioning(true);
    setCurrentSlide(index);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  };

  // Handle navigate to slideshow management
  const handleManageSlideshow = () => {
    navigate('/admin?tab=slideshow');
  };
  
  // Loading state
  if (loading) {
    return <div className="slideshow-loading">Loading slideshow...</div>;
  }
  
  // Error state
  if (error) {
    return <div className="slideshow-error">Error loading slideshow: {error}</div>;
  }
  
  return (
    <div className="slideshow-container">
      {/* Admin Controls */}
      {isAdmin && (
        <div className="slideshow-admin-controls">
          <button 
            className="add-slideshow-btn" 
            onClick={handleManageSlideshow}
            style={{
              background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.8rem 1.5rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 5px 15px rgba(56, 239, 125, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              position: 'absolute',
              top: '15px',
              right: '15px',
              zIndex: 10
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>➕</span>
            {activeSlides.length === 0 ? 'Add Slideshow' : 'Manage Slideshow'}
          </button>
        </div>
      )}
      
      {/* No slides available */}
      {activeSlides.length === 0 ? (
        isAdmin ? (
          <div className="empty-slideshow-placeholder" style={{
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            border: '2px dashed #ccc'
          }}>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ marginBottom: '15px', color: '#666' }}>No slideshow images available</h3>
              <p style={{ color: '#888', marginBottom: '20px' }}>Click the button above to add your first slideshow image.</p>
            </div>
          </div>
        ) : null // Don't show anything to non-admins if no slides
      ) : (
        <div className="slideshow">
          {activeSlides.map((slide, index) => (
            <div 
              key={slide.id} 
              className={`slide ${index === currentSlide ? 'active' : ''}`}
              style={{ transform: `translateX(${100 * (index - currentSlide)}%)` }}
            >
              <img 
                src={slide.imageBase64} 
                alt={slide.title || 'Slideshow image'} 
                className="slide-image"
              />
              
              {(slide.title || slide.description) && (
                <div className="slide-caption">
                  {slide.title && <h3>{slide.title}</h3>}
                  {slide.description && <p>{slide.description}</p>}
                </div>
              )}
            </div>
          ))}
          
          {/* Navigation Arrows */}
          {activeSlides.length > 1 && (
            <>
              <button 
                className="slide-arrow prev"
                onClick={goToPrevSlide}
                disabled={isTransitioning}
              >
                &#10094;
              </button>
              
              <button 
                className="slide-arrow next"
                onClick={goToNextSlide}
                disabled={isTransitioning}
              >
                &#10095;
              </button>
              
              {/* Dots/indicators */}
              <div className="slide-dots">
                {activeSlides.map((_, index) => (
                  <button
                    key={index}
                    className={`slide-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    disabled={isTransitioning}
                  ></button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Slideshow; 